<template>
  <DropdownGptSharingItem
    v-if="isFoundever"
    :key="selectedOption.label"
    :label="selectedOption.label"
    :description="selectedOption.description"
    :icon="selectedOption.icon"
    :isTopbarDisplay="true"
    class="DropdownGptSharing__active-item"
    @click.prevent
  />
  <UIDropdown
    v-else
    :items="dropdownItems"
    class="DropdownGptSharing"
    v-bind="$attrs"
    @clickedOption="onSelectOption"
  >
    <!-- Inside ui-lib's toggle, which supplies the chevron, focus and Enter / Space /
         ArrowDown. Its field border and fill are dropped below: this one reads as the
         current value, not as a form field. -->
    <template #toggle>
      <DropdownGptSharingItem
        v-if="selectedOption"
        :key="selectedOption.label"
        :label="selectedOption.label"
        :description="selectedOption.description"
        :icon="selectedOption.icon"
        :isTopbarDisplay="true"
        class="DropdownGptSharing__active-item"
      />
    </template>

    <template #option="{ option }">
      <DropdownGptSharingItem
        v-if="optionByValue[option.value]"
        class="DropdownGptSharing__list-item"
        :label="optionByValue[option.value].label"
        :description="optionByValue[option.value].description"
        :icon="optionByValue[option.value].icon"
        :isSelected="selectedOption.value === option.value"
      />
    </template>
  </UIDropdown>

  <ModalConfirm
    :isOpen="modalConfirmation.isOpen"
    confirmButtonLabel="Remove"
    @close="modalConfirmation.close()"
    @confirm="removePublicSharing"
  >
    <template #title> Remove sharing with everyone? </template>
    <template #content>
      You’re about to remove sharing this GPT with everyone and restrict access
      to selected users only.
    </template>
  </ModalConfirm>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'

import { UIDropdown } from '@foundever/ui-lib'

import { useModal } from '@/composables/useModal'

import DropdownGptSharingItem from './DropdownGptSharingItem.vue'
import ModalConfirm from '../modals/ModalConfirm.vue'

import type { IIcons } from '../ui/icons/types'

defineOptions({
  inheritAttrs: false,
})
const emit = defineEmits(['update:modelValue'])
const props = defineProps<{
  modelValue: boolean
  isFoundever?: boolean
}>()

interface IFilteredOption {
  label: string
  value: 'everyone' | 'private'
  description: string
  icon: IIcons
}

const filteredOptions = computed<IFilteredOption[]>(() => [
  {
    label: 'Everyone',
    value: 'everyone',
    description: 'Anyone has access to this GPT.',
    icon: 'globe' as IIcons,
  },
  {
    label: 'Private',
    value: 'private',
    description: 'Only you or specific people can view this GPT.',
    icon: 'lock' as IIcons,
  },
])

// ui-lib's rows carry only `label` / `value`; the rich row is looked up by value.
const dropdownItems = computed(() =>
  filteredOptions.value.map(({ label, value }) => ({ label, value })),
)
const optionByValue = computed(
  () =>
    Object.fromEntries(
      filteredOptions.value.map((option) => [option.value, option]),
    ) as Record<string, IFilteredOption>,
)

const selectedOption = ref<IFilteredOption>(
  filteredOptions.value[filteredOptions.value.length - 1],
)
const setGptSharing = (option: IFilteredOption) => {
  const isPublic = option.value === 'everyone'
  if (props.modelValue === isPublic) return

  selectedOption.value = option
  emit('update:modelValue', isPublic)
}

const modalConfirmation = useModal<IFilteredOption>()
// Lets the parent modal stay open while the nested confirm handles Escape
defineExpose({ isConfirmOpen: computed(() => modalConfirmation.isOpen) })
const onSelectOption = (picked: { value: string }) => {
  const option = optionByValue.value[picked.value]
  if (option) openConfirmationModal(option)
}
const openConfirmationModal = (option: IFilteredOption) => {
  if (props.modelValue && option.value === 'private') {
    modalConfirmation.open(option)
  } else {
    setGptSharing(option)
  }
}

const removePublicSharing = () => {
  if (modalConfirmation.modalData) {
    setGptSharing(modalConfirmation.modalData)
  }
  modalConfirmation.close()
}

watch(
  () => props.modelValue,
  (value) => {
    const selected = filteredOptions.value.find((option) =>
      value ? option.value === 'everyone' : option.value === 'private',
    )

    if (selected) {
      selectedOption.value = selected
    }
  },
  { immediate: true },
)
</script>

<style scoped lang="scss">
.DropdownGptSharing {
  :deep(.UIDropdown__trigger) {
    width: 100%;
  }

  :deep(.UIDropdownToggle) {
    height: auto;
    min-width: 0;
    padding: 0 var(---fds-global-space-xs) 0 0;
    border-color: transparent;
    border-radius: var(--radius-s);
    background-color: transparent;

    &:hover {
      background-color: var(--bg-secondary);
    }
  }

  .DropdownGptSharing__active-item {
    padding: 0.5rem;
    align-items: center;
  }
}
</style>

<style lang="scss">
// ui-lib sizes the teleported panel to `min-width: <trigger width>` and keeps it in
// step with the trigger, so the menu is as wide as the closed control. `min-content`
// stops the longer description from widening it past that; `border-box` makes the
// min-width land on the panel's outer edge.
.UIDropdown__content:has(.DropdownGptSharing__list-item) {
  box-sizing: border-box;
  width: min-content;
}

.UIDropdown__content .UIDropdownListItem:has(.DropdownGptSharing__list-item) {
  min-width: 0;
  padding: var(---fds-global-space-xs);
}
</style>
